// Regular-expression testing, pure and client-side (native RegExp).

export interface RegexMatch {
  index: number;
  length: number;
  value: string;
  groups: string[];
}

export interface RegexResult {
  error: string | null;
  matches: RegexMatch[];
}

export const REGEX_FLAGS = ['g', 'i', 'm', 's', 'u', 'y'] as const;
export type RegexFlag = (typeof REGEX_FLAGS)[number];

/** Runs the pattern over the text and returns all matches (or a syntax error). */
export function runRegex(pattern: string, flags: string, text: string): RegexResult {
  if (!pattern) return { error: null, matches: [] };

  // Iterate globally so the tester shows every match (unless sticky is requested).
  const effective = /[gy]/.test(flags) ? flags : `${flags}g`;
  let re: RegExp;
  try {
    re = new RegExp(pattern, effective);
  } catch (e) {
    return { error: (e as Error).message, matches: [] };
  }

  const matches: RegexMatch[] = [];
  let m: RegExpExecArray | null;
  let guard = 0;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard exec loop
  while ((m = re.exec(text)) !== null) {
    matches.push({
      index: m.index,
      length: m[0].length,
      value: m[0],
      groups: m.slice(1).map((g) => g ?? ''),
    });
    if (m.index === re.lastIndex) re.lastIndex++; // avoid zero-length infinite loop
    if (++guard > 10000) break;
  }
  return { error: null, matches };
}

export interface Segment {
  text: string;
  match: boolean;
}

/** Splits the text into matched / unmatched segments for highlighting. */
export function toSegments(text: string, matches: RegexMatch[]): Segment[] {
  if (matches.length === 0) return text ? [{ text, match: false }] : [];
  const segments: Segment[] = [];
  let pos = 0;
  for (const m of matches) {
    if (m.index > pos) segments.push({ text: text.slice(pos, m.index), match: false });
    segments.push({ text: text.slice(m.index, m.index + m.length), match: true });
    pos = m.index + m.length;
  }
  if (pos < text.length) segments.push({ text: text.slice(pos), match: false });
  return segments;
}
